import React, { useState, useEffect } from "react";
import { MessageCircle, Download } from "lucide-react";
import { CommentThread } from "../CommentThread";
import type { Comment } from "../../services/comments";

interface CollapsibleCommentsPanelProps {
  expanded: boolean;
  onToggle: () => void;
  comments: Comment[];
  onNewComment: () => void;
  newComment: string;
  onNewCommentChange: (value: string) => void;
  isSubmittingComment: boolean;
  onReplySubmit: (content: string, parentId: number) => Promise<void>;
  onDownload?: () => void;
  canDownload?: boolean;
  className?: string;
}

const CollapsibleCommentsPanel: React.FC<CollapsibleCommentsPanelProps> = ({
  expanded,
  onToggle,
  comments,
  onNewComment,
  newComment,
  onNewCommentChange,
  isSubmittingComment,
  onReplySubmit,
  onDownload,
  canDownload = false,
  className = "",
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768
  );

  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), 300);
    return () => clearTimeout(timer);
  }, [expanded]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className={`flex flex-col ${className}`}>
      {/* Header */}
      <div
        onClick={onToggle}
        className={`flex items-center justify-between px-4 py-3 bg-gray-50 border-t cursor-pointer hover:bg-gray-100 transition-colors duration-200 ${
          expanded ? "border-b" : ""
        }`}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`${expanded ? "Chiudi" : "Espandi"} pannello commenti`}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <div className="flex items-center gap-3">
          <MessageCircle className="text-gray-500" size={20} />
          <h2 className="text-lg font-semibold">
            Commenti ({comments.length})
          </h2>
          {!isMobile && (
            <span className="text-xs text-gray-400 bg-gray-200 px-2 py-1 rounded">
              Premi C
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          {!expanded && canDownload && onDownload && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDownload();
              }}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Download"
            >
              <Download size={18} />
            </button>
          )}

          {!expanded && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              Nuovo commento...
            </button>
          )}
        </div>
      </div>

      {/* Collapsible Content */}
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${expanded ? "max-h-[9999px]" : "max-h-0"}`}
        style={{
          opacity: expanded ? 1 : 0,
          transitionProperty: "max-height, opacity",
          transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div className="flex flex-col bg-white">
          {/* New Comment Form */}
          <div className="flex-shrink-0 p-4 border-b bg-gray-50">
            <textarea
              value={newComment}
              onChange={(e) => onNewCommentChange(e.target.value)}
              placeholder="Aggiungi un commento..."
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              rows={2}
            />
            <div className="flex justify-between items-center mt-3">
              <div className="flex items-center gap-2">
                {canDownload && onDownload && (
                  <button
                    onClick={onDownload}
                    className="flex items-center gap-2 px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Download size={16} />
                    Download
                  </button>
                )}
              </div>
              <button
                onClick={onNewComment}
                disabled={!newComment.trim() || isSubmittingComment}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmittingComment ? "Invio..." : "Invia Commento"}
              </button>
            </div>
          </div>

          {/* Comments List */}
          <div className={`p-4 ${isAnimating ? "pointer-events-none" : ""}`}>
            {comments.length > 0 ? (
              <div className="space-y-4">
                {comments.map((comment) => (
                  <CommentThread
                    key={comment.id}
                    comment={comment}
                    onReplySubmit={onReplySubmit}
                  />
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center py-12">
                <div className="text-center text-gray-500">
                  <MessageCircle
                    className="mx-auto mb-2 opacity-50"
                    size={48}
                  />
                  <p>Nessun commento ancora</p>
                  <p className="text-sm">Aggiungi il primo commento sopra</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollapsibleCommentsPanel;
